// Poll overlay: category (6) or clue-value (5) options with live vote bars +
// live vote-count circles (v1.3, GDD §11 visual brief). When a poll_open frame
// arrives, each option shows a small filled circle above it; counts arrive via
// poll_tally_update messages and tween to the new value over 250ms. Color tracks
// relative rank (gold=leader, blue=mid, dim=trailing). On poll_close the
// winner's circle briefly highlights.

import {
  CircleGeometry,
  Color,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  type Texture,
} from 'three';
import type { PollOption } from '@sg/shared';
import { clamp01, easeOutCubic } from './lib/easing.js';
import { textTexture } from './lib/text-texture.js';
import {
  CARD_RADIUS,
  CARD_SHADOW_BLUR,
  CARD_SHADOW_COLOR,
  CARD_SHADOW_OFFSET_Y,
  COLOR_GOLD,
  COLOR_STAGE_BLUE,
  COLOR_PANEL_BG,
  COLOR_PANEL_BORDER,
  FONT_OSWALD,
  FONT_UI,
} from './lib/theme.js';

const CARD_W = 320;
const CARD_H = 150;
const BAR_H = 18;
const CIRCLE_R_MIN = 18;
const CIRCLE_R_MAX = 36;
const CIRCLE_TWEEN_MS = 250;
const WINNER_HIGHLIGHT_MS = 1200;

export interface PollOverlayHandle {
  group: Object3D;
  setPhase: (phase: 'category' | 'clue_value') => void;
  setOptions: (options: PollOption[]) => void;
  setTallies: (tallies: Record<string, number>) => void;
  setWinner: (key: string | null) => void;
  /** Per-frame tick for tween animations (live counts, winner pulse). */
  tick: (nowMs: number) => void;
  show: () => void;
  hide: () => void;
  dispose: () => void;
}

interface Row {
  key: string;
  card: Mesh;
  labelMesh: Mesh;
  barMesh: Mesh;
  /** Filled circle hosting the running vote count. */
  circle: Mesh;
  circleMat: MeshBasicMaterial;
  /** Text mesh showing the integer count inside the circle. */
  countMesh: Mesh;
  countMat: MeshBasicMaterial;
  /** Anchor x/y of the count column (above the card). */
  circleX: number;
  circleY: number;
  count: number;
  /** Tween bookkeeping for the displayed (interpolated) count. */
  displayedCount: number;
  tweenFrom: number;
  tweenStartMs: number | null;
  /** Winner-pulse start; null when inactive. */
  winnerPulseStartMs: number | null;
}

export function createPollOverlay(): PollOverlayHandle {
  const group = new Object3D();
  group.name = 'poll-overlay';
  group.visible = false;

  const rows: Row[] = [];
  let currentOptions: PollOption[] = [];
  let winner: string | null = null;

  function clearRows(): void {
    for (const r of rows) {
      (r.card.material as MeshBasicMaterial).dispose();
      r.card.geometry.dispose();
      (r.labelMesh.material as MeshBasicMaterial).dispose();
      r.labelMesh.geometry.dispose();
      (r.barMesh.material as MeshBasicMaterial).dispose();
      r.barMesh.geometry.dispose();
      r.circleMat.dispose();
      r.circle.geometry.dispose();
      r.countMat.dispose();
      r.countMesh.geometry.dispose();
      group.remove(r.card);
      group.remove(r.labelMesh);
      group.remove(r.barMesh);
      group.remove(r.circle);
      group.remove(r.countMesh);
    }
    rows.length = 0;
  }

  function buildRow(i: number, total: number, opt: PollOption): Row {
    const perRow = Math.ceil(total / 2);
    const col = i % perRow;
    const row = Math.floor(i / perRow);
    const spacingX = CARD_W + 40;
    const spacingY = CARD_H + 60;
    const x = (col - (perRow - 1) / 2) * spacingX;
    const y = -row * spacingY + spacingY / 2;

    // Card background: drawn as a rounded-rect texture on the mesh so
    // every overlay card matches the board tile + lobby card look.
    const cardBgTex = textTexture('', {
      font: `1px ${FONT_UI}`,
      color: 'rgba(0,0,0,0)',
      bg: cardBgHex(),
      width: CARD_W,
      height: CARD_H,
      borderRadius: CARD_RADIUS,
      shadowColor: CARD_SHADOW_COLOR,
      shadowBlur: CARD_SHADOW_BLUR,
      shadowOffsetY: CARD_SHADOW_OFFSET_Y,
      autoFit: false,
    });
    const cardGeom = new PlaneGeometry(CARD_W, CARD_H);
    const cardMat = new MeshBasicMaterial({ color: 0xffffff, map: cardBgTex, transparent: true, opacity: 0.95, depthWrite: false });
    const card = new Mesh(cardGeom, cardMat);
    card.position.set(x, y, 0);
    group.add(card);

    const labelTex: Texture = textTexture(opt.label.toUpperCase(), {
      font: `42px ${FONT_OSWALD}`,
      color: '#FFFFFF',
      width: CARD_W - 20,
      height: 60,
      weight: 'bold',
      autoFit: true,
      minFontPx: 20,
    });
    const labelMat = new MeshBasicMaterial({ transparent: true, map: labelTex, depthWrite: false });
    const labelMesh = new Mesh(new PlaneGeometry(CARD_W - 20, 60), labelMat);
    labelMesh.position.set(x, y + 30, 0.1);
    group.add(labelMesh);

    const barGeom = new PlaneGeometry(CARD_W - 40, BAR_H);
    const barMat = new MeshBasicMaterial({ color: COLOR_STAGE_BLUE, transparent: true });
    const barMesh = new Mesh(barGeom, barMat);
    barMesh.position.set(x - (CARD_W - 40) / 2, y - 30, 0.1);
    barMesh.scale.x = 0.001;
    group.add(barMesh);

    // Live-count circle: positioned above the card.
    const circleY = y + CARD_H / 2 + CIRCLE_R_MAX + 4;
    const circleGeom = new CircleGeometry(CIRCLE_R_MIN, 32);
    const circleMat = new MeshBasicMaterial({ color: COLOR_PANEL_BORDER, transparent: true, opacity: 0.95 });
    const circle = new Mesh(circleGeom, circleMat);
    circle.position.set(x, circleY, 0.2);
    group.add(circle);

    const countTex = textTexture('0', {
      font: `28px ${FONT_UI}`,
      color: '#FFFFFF',
      width: CIRCLE_R_MAX * 2,
      height: CIRCLE_R_MAX * 2,
      weight: 'bold',
    });
    const countMat = new MeshBasicMaterial({ transparent: true, map: countTex, depthWrite: false });
    const countMesh = new Mesh(new PlaneGeometry(CIRCLE_R_MAX * 2, CIRCLE_R_MAX * 2), countMat);
    countMesh.position.set(x, circleY, 0.3);
    group.add(countMesh);

    return {
      key: opt.key,
      card,
      labelMesh,
      barMesh,
      circle,
      circleMat,
      countMesh,
      countMat,
      circleX: x,
      circleY,
      count: 0,
      displayedCount: 0,
      tweenFrom: 0,
      tweenStartMs: null,
      winnerPulseStartMs: null,
    };
  }

  function setOptions(options: PollOption[]): void {
    clearRows();
    currentOptions = options;
    options.forEach((o, i) => {
      rows.push(buildRow(i, options.length, o));
    });
    winner = null;
    applyWinnerStyling();
    refreshRanks();
  }

  function setPhase(_phase: 'category' | 'clue_value'): void {
    // Styling differences live here if we want to visually distinguish.
    void _phase;
  }

  function setTallies(tallies: Record<string, number>): void {
    const max = Math.max(1, ...Object.values(tallies));
    for (const r of rows) {
      const v = tallies[r.key] ?? 0;
      // Tween: start a fresh interpolation from current displayed value.
      if (v !== r.count) {
        r.tweenFrom = r.displayedCount;
        r.tweenStartMs = performance.now();
      }
      r.count = v;
      const t = v / max;
      const targetScaleX = Math.max(0.001, t);
      r.barMesh.scale.x = targetScaleX;
      // Anchor left — reposition so bar grows right.
      const originX = r.card.position.x - (CARD_W - 40) / 2;
      r.barMesh.position.x = originX + ((CARD_W - 40) * targetScaleX) / 2 - (CARD_W - 40) / 2;
    }
    refreshRanks();
  }

  function refreshRanks(): void {
    // Re-color circles by relative rank: leader = gold, others = blue, zero = dim.
    const sorted = [...rows].sort((a, b) => b.count - a.count);
    const top = sorted[0]?.count ?? 0;
    for (const r of rows) {
      let color = COLOR_PANEL_BORDER;
      if (top > 0) {
        if (r.count === top) color = COLOR_GOLD;
        else if (r.count > 0) color = COLOR_STAGE_BLUE;
      }
      // Don't override during winner-pulse highlight.
      if (r.winnerPulseStartMs === null) {
        r.circleMat.color = new Color(color);
      }
      // Scale circle radius by count (clamped). Visual cue beyond just text.
      const radius = scaleRadius(r.count, top);
      r.circle.scale.setScalar(radius / CIRCLE_R_MIN);
    }
  }

  function scaleRadius(count: number, top: number): number {
    if (top <= 0) return CIRCLE_R_MIN;
    const t = clamp01(count / top);
    return CIRCLE_R_MIN + (CIRCLE_R_MAX - CIRCLE_R_MIN) * t;
  }

  function setWinner(key: string | null): void {
    winner = key;
    applyWinnerStyling();
    if (key !== null) {
      const w = rows.find((r) => r.key === key);
      if (w) w.winnerPulseStartMs = performance.now();
    }
  }

  function applyWinnerStyling(): void {
    // Card backgrounds are now map-backed (rounded rect + shadow rasterised
    // once). We tint via material.color (multiplies against the map) instead
    // of swapping the hex outright — preserves the rounded corner alpha.
    for (const r of rows) {
      const mat = r.card.material as MeshBasicMaterial;
      if (winner === null) {
        mat.color = new Color(0xffffff);
        mat.opacity = 0.95;
      } else if (r.key === winner) {
        mat.color = new Color(COLOR_GOLD);
        mat.opacity = 1;
      } else {
        mat.color = new Color(0xffffff);
        mat.opacity = 0.45;
      }
    }
  }

  /** Read the panel-bg colour as a hex string for canvas fill. */
  function cardBgHex(): string {
    return '#' + COLOR_PANEL_BG.toString(16).padStart(6, '0');
  }

  function redrawCount(r: Row, displayed: number): void {
    const intVal = Math.round(displayed);
    if (r.countMat.map) r.countMat.map.dispose();
    const tex = textTexture(`${intVal}`, {
      font: `28px ${FONT_UI}`,
      color: '#FFFFFF',
      width: CIRCLE_R_MAX * 2,
      height: CIRCLE_R_MAX * 2,
      weight: 'bold',
    });
    r.countMat.map = tex;
    r.countMat.needsUpdate = true;
  }

  function tick(nowMs: number): void {
    let anyChanged = false;
    for (const r of rows) {
      if (r.tweenStartMs !== null) {
        const t = clamp01((nowMs - r.tweenStartMs) / CIRCLE_TWEEN_MS);
        const e = easeOutCubic(t);
        const v = r.tweenFrom + (r.count - r.tweenFrom) * e;
        if (Math.round(v) !== Math.round(r.displayedCount)) anyChanged = true;
        r.displayedCount = v;
        redrawCount(r, v);
        if (t >= 1) {
          r.displayedCount = r.count;
          r.tweenStartMs = null;
        }
      }
      if (r.winnerPulseStartMs !== null) {
        const t = clamp01((nowMs - r.winnerPulseStartMs) / WINNER_HIGHLIGHT_MS);
        // Pulse: gold at peak, then settle back to gold (winner stays highlighted).
        const e = 0.5 + 0.5 * Math.sin(t * Math.PI * 4);
        const opacity = 0.7 + 0.3 * e;
        r.circleMat.color = new Color(COLOR_GOLD);
        r.circleMat.opacity = opacity;
        r.circle.scale.setScalar(((CIRCLE_R_MAX + 6) / CIRCLE_R_MIN));
        if (t >= 1) {
          r.circleMat.opacity = 1;
          r.winnerPulseStartMs = null;
        }
      }
    }
    void anyChanged;
  }

  void FONT_UI;
  void currentOptions;

  return {
    group,
    setPhase,
    setOptions,
    setTallies,
    setWinner,
    tick,
    show() {
      group.visible = true;
    },
    hide() {
      group.visible = false;
    },
    dispose() {
      clearRows();
    },
  };
}
